import React, { memo, useMemo, useEffect, useContext, useCallback } from 'react'
import {
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_ALWAYS_ON,
} from 'constants/offer'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import { buildSearchStringFromFilters } from 'lib/search/searchUtils'

import Heading from 'components/Luxkit/Typography/Heading'
import useOfferList from 'hooks/Offers/useOfferList'
import { skip, take, without } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getGraphRecommendations } from 'selectors/recomendations/graphSelectors'
import OfferListEventsContext, { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import { rem } from 'polished'
import styled from 'styled-components'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import OfferList, { OfferListAction, OfferListAndAction } from 'components/OfferList/OfferList'
import { saveDefaultRecommendation } from 'actions/RecommendationActions'
import { OptimizelyServerSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import * as Analytics from 'analytics/analytics'
import { clickGraphRecommendation, viewGraphRecommendation } from 'analytics/optimizely/events'
import { useInView } from 'react-intersection-observer'
import useOffers from 'hooks/Offers/useOffers'

const RecommendationsSection = styled(Pane)`
  padding: ${rem(40)} 0;
`

const emptyFilters: App.OfferListFilters = EmptyObject

interface Props {
  title?: string;
  offerId?: string;
  offerType?: App.OfferType;
  filters?: App.OfferListFilters;
  trackingDescription: string;
  useGraph?: boolean;
  skipFirstNOffers?: number;
  useList?: boolean;
  overrideOfferList?: App.OfferList;
  maxOffers?: number;
  canJoinLereGraphAbTest?: boolean
}

const MINIMUM_OFFERS_TO_DISPLAY = 2
const DEFAULT_MAX_OFFERS = 8

const RECOMMENDATION_TYPES: App.OfferType[] = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_LAST_MINUTE,
  'bedbank_hotel',
]

function OfferRecommendations(props: Props) {
  const {
    title = 'You may also like',
    offerId,
    filters = emptyFilters,
    trackingDescription,
    useGraph = false,
    skipFirstNOffers,
    useList = false,
    overrideOfferList,
    maxOffers = DEFAULT_MAX_OFFERS,
    canJoinLereGraphAbTest = false,
  } = props

  const useOverrideList = !!overrideOfferList?.fetching || !!overrideOfferList?.offerIds?.length
  const defaultOfferList = useOfferList({ ...filters, offerTypes: RECOMMENDATION_TYPES }, { disabled: useGraph || useOverrideList })
  const offerList: App.OfferList = useMemo(() => {
    if (useOverrideList) {
      return overrideOfferList
    }
    return defaultOfferList
  }, [defaultOfferList, overrideOfferList, useOverrideList])
  const { offerIds: graphOfferIds, state: graphState } = useAppSelector(state => getGraphRecommendations(state, offerId))

  const dispatch = useAppDispatch()
  useEffect(() => {
    if (offerId && graphOfferIds.length && graphState === 'done') {
      dispatch(saveDefaultRecommendation(offerId, graphOfferIds))
    }
  }, [dispatch, offerId, graphOfferIds, graphState])

  const offerIds = useMemo(() => {
    let offerCandidates = useGraph ? graphOfferIds : offerList.offerIds

    // remove source offer
    offerCandidates = without(offerCandidates, offerId ?? '')

    // skip first few offers if specified
    if (skipFirstNOffers) {
      offerCandidates = skip(offerCandidates, skipFirstNOffers)
    }

    // take max offers
    offerCandidates = take(offerCandidates, maxOffers)

    return offerCandidates
  }, [offerId, offerList.offerIds, useGraph, graphOfferIds, skipFirstNOffers, maxOffers])

  const [offers, offersFetching] = useOffers(offerIds)

  const algorithm = useGraph ? 'Graph' : (filters.propertyId ? 'Search Nearby' : 'Algolia')
  const tracking = useOfferListTracking(
    `Recommendations-${algorithm}-${trackingDescription}`, {
      ga: {
        category: 'Recommendations',
        label: `${algorithm}-${trackingDescription}`,
      },
    })

  const isFetching = (
    (useGraph && graphState === 'loading') ||
    (!useGraph && offerList.fetching) ||
    offersFetching
  )

  // inherit parent context
  const offerListAction = useContext(OfferListEventsContext)
  const InsuffientReco = offers.length < MINIMUM_OFFERS_TO_DISPLAY && !isFetching

  const [ref, inView] = useInView({ triggerOnce: true })
  const canAbTestLereGraph = !!canJoinLereGraphAbTest && inView && useGraph && !InsuffientReco && !isFetching
  // only trigger experiment view the recommendation section and have the graph recommendations
  useOptimizelyExperiment(OptimizelyServerSideExperiments.lereGraphRecommendation, canAbTestLereGraph)

  const onListEvent = useCallback((action: OfferListEvents, data?: any, additionalData?: any) => {
    // parent offerList
    offerListAction(action, data, additionalData)
    // if eligible users for lere graph ab test, send the optimisely events
    if (canAbTestLereGraph) {
      if (action === OfferListEvents.productClick) {
        Analytics.track('click-graph-recommendation-on-offer-page', clickGraphRecommendation())
      }
      if (action === OfferListEvents.impression) {
        // The visitor event and offer view event almost fires at the same time when the carousel come into view
        // but if offer view event is before visitor event it won't count in optimizely
        // so we have to delay offer view event to avoid race condition with experiment trigger
        setTimeout(() => Analytics.track('view-graph-recommendation-on-offer-page', viewGraphRecommendation()), 500)
      }
    }
  }, [canAbTestLereGraph, offerListAction])

  const additionalQueryParams = useMemo(() => buildSearchStringFromFilters(filters), [filters])

  const toShowOfferList = useMemo<App.OfferList>(() => ({
    fetching: isFetching,
    offerIds,
    key: 'offerRecommendations',
  }), [isFetching, offerIds])

  const overrideOfferListAndAction = useMemo<OfferListAndAction>(() => ({
    action: OfferListAction.OVERRIDE,
    offerList: toShowOfferList,
  }), [toShowOfferList])

  if (InsuffientReco) {
    return null
  }

  return (
    <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
      <RecommendationsSection ref={ref}>
        <VerticalSpacer gap={16}>
          <LayoutContainer>
            <Heading variant="heading3">{title}</Heading>
          </LayoutContainer>
          {!useList && <OfferListCarousel
            overrideOfferList={toShowOfferList}
            additionalOfferUrlQuery={additionalQueryParams}
          />}
          {useList && <OfferList
            extraOfferListAndAction={overrideOfferListAndAction}
            tileStyle="search"
          />}
        </VerticalSpacer>
      </RecommendationsSection>
    </OfferListEventsProvider>
  )
}

export default memo(OfferRecommendations)
